import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StudentFeeLedgerResponseDto } from "../../shared/dtos/studentFeeLedger/StudentFeeLedgerResponseDto";
import { FeeStatementDto } from "../../shared/dtos/studentFeeLedger/FeeStatementDto";
import { BulkPaymentUploadResponseDto } from "../../shared/dtos/studentFeeLedger/BulkPaymentUploadResponseDto";
import { PaginationDto } from "../../shared/dtos/common/PaginationDto";
import { ServerStatusPayload } from "../../shared/interfaces/ServerStatusPayload";

export interface StudentFeeLedgerState {
  // Statement (Individual Student)
  loadingStatement: boolean;
  errorLoadingStatement: string | null;
  studentStatement: FeeStatementDto | null;

  // Global Ledger (Admin List)
  loadingLedgerEntries: boolean;
  errorLoadingLedgerEntries: string | null;
  ledgerEntries: StudentFeeLedgerResponseDto[];
  pagination: PaginationDto | null;

  // Single Transaction operations
  loadingTransactionCreate: boolean;
  errorCreatingTransaction: string | null;

  loadingTransactionDelete: boolean;
  errorDeletingTransaction: string | null;

  // Batch operations
  loadingBatchGeneration: boolean;
  errorBatchGeneration: string | null;
  batchGenerationResult: string | null;

  loadingReminders: boolean;
  errorReminders: string | null;
  remindersResult: string | null;

  // Upload operations
  loadingUpload: boolean;
  errorUpload: string | null;
  uploadResult: BulkPaymentUploadResponseDto | null;

  // PDF
  loadingPdf: boolean;
  errorPdf: string | null;
  pdfUrl: string | null;

  // Email
  loadingEmail: boolean;
  errorEmail: string | null;

  serverStatus: ServerStatusPayload | null;
}

const initialState: StudentFeeLedgerState = {
  loadingStatement: false,
  errorLoadingStatement: null,
  studentStatement: null,

  loadingLedgerEntries: false,
  errorLoadingLedgerEntries: null,
  ledgerEntries: [],
  pagination: null,

  loadingTransactionCreate: false,
  errorCreatingTransaction: null,

  loadingTransactionDelete: false,
  errorDeletingTransaction: null,

  loadingBatchGeneration: false,
  errorBatchGeneration: null,
  batchGenerationResult: null,

  loadingReminders: false,
  errorReminders: null,
  remindersResult: null,

  loadingUpload: false,
  errorUpload: null,
  uploadResult: null,

  loadingPdf: false,
  errorPdf: null,

  loadingEmail: false,
  errorEmail: null,
  pdfUrl: null,

  serverStatus: null,
};

export const studentFeeLedgerSlice = createSlice({
  name: "studentFeeLedger",
  initialState,
  reducers: {
    // STATEMENT
    setLoadingStatement(state) {
      state.loadingStatement = true;
      state.errorLoadingStatement = null;
    },
    setStudentStatement(state, action: PayloadAction<FeeStatementDto>) {
      state.loadingStatement = false;
      state.studentStatement = action.payload;
      state.errorLoadingStatement = null;
    },
    setErrorLoadingStatement(state, action: PayloadAction<string | null>) {
      state.loadingStatement = false;
      state.errorLoadingStatement = action.payload;
    },

    // LEDGER ENTRIES
    setLoadingLedgerEntries(state) {
      state.loadingLedgerEntries = true;
      state.errorLoadingLedgerEntries = null;
    },
    setLedgerEntries(
      state,
      action: PayloadAction<StudentFeeLedgerResponseDto[]>,
    ) {
      state.loadingLedgerEntries = false;
      state.ledgerEntries = action.payload;
      state.errorLoadingLedgerEntries = null;
    },
    setPagination(state, action: PayloadAction<PaginationDto>) {
      state.pagination = action.payload;
    },
    setErrorLoadingLedgerEntries(state, action: PayloadAction<string | null>) {
      state.loadingLedgerEntries = false;
      state.errorLoadingLedgerEntries = action.payload;
    },

    // TRANSACTIONS
    setLoadingTransactionCreate(state) {
      state.loadingTransactionCreate = true;
      state.errorCreatingTransaction = null;
    },
    createTransactionSuccess(state) {
      state.loadingTransactionCreate = false;
      state.errorCreatingTransaction = null;
    },
    setErrorCreatingTransaction(state, action: PayloadAction<string | null>) {
      state.loadingTransactionCreate = false;
      state.errorCreatingTransaction = action.payload;
    },

    // BATCH
    setLoadingBatchGeneration(state) {
      state.loadingBatchGeneration = true;
      state.errorBatchGeneration = null;
      state.batchGenerationResult = null;
    },
    setBatchGenerationSuccess(state, action: PayloadAction<string>) {
      state.loadingBatchGeneration = false;
      state.batchGenerationResult = action.payload;
    },
    setErrorBatchGeneration(state, action: PayloadAction<string | null>) {
      state.loadingBatchGeneration = false;
      state.errorBatchGeneration = action.payload;
    },

    // REMINDERS
    setLoadingReminders(state) {
      state.loadingReminders = true;
      state.errorReminders = null;
      state.remindersResult = null;
    },
    setRemindersSuccess(state, action: PayloadAction<string>) {
      state.loadingReminders = false;
      state.remindersResult = action.payload;
    },
    setErrorReminders(state, action: PayloadAction<string | null>) {
      state.loadingReminders = false;
      state.errorReminders = action.payload;
    },

    // UPLOAD
    setLoadingUpload(state) {
      state.loadingUpload = true;
      state.errorUpload = null;
      state.uploadResult = null;
    },
    setUploadSuccess(
      state,
      action: PayloadAction<BulkPaymentUploadResponseDto>,
    ) {
      state.loadingUpload = false;
      state.uploadResult = action.payload;
    },
    setErrorUpload(state, action: PayloadAction<string | null>) {
      state.loadingUpload = false;
      state.errorUpload = action.payload;
    },

    // COMMON
    setServerResponseStatus(
      state,
      action: PayloadAction<ServerStatusPayload | null>,
    ) {
      state.serverStatus = action.payload;
    },

    // PDF && Email
    setLoadingPdf(state) {
      state.loadingPdf = true;
      state.errorPdf = null;
    },
    setLoadingEmail(state) {
      state.loadingEmail = true;
      state.errorEmail = null;
    },
    setErrorPdf(state, action: PayloadAction<string | null>) {
      state.loadingPdf = false;
      state.errorPdf = action.payload;
    },
    setErrorEmail(state, action: PayloadAction<string | null>) {
      state.loadingEmail = false;
      state.errorEmail = action.payload;
    },

    setPdfUrl(state, action: PayloadAction<string>) {
      state.loadingPdf = false;
      state.pdfUrl = action.payload;
    },
    clearPdfUrl(state) {
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      state.pdfUrl = null;
    },
    setEmailSuccess(state) {
      state.loadingEmail = false;
    },

    resetFinanceState(state) {
      state.batchGenerationResult = null;
      state.remindersResult = null;
      state.uploadResult = null;
      state.errorUpload = null;
      state.serverStatus = null;
    },
  },
});

export const studentFeeLedgerActions = studentFeeLedgerSlice.actions;
export default studentFeeLedgerSlice.reducer;
